// Lightweight document-head manager — no extra deps.
// Sets/updates <title>, meta description, canonical, and OpenGraph + Twitter tags
// per route at runtime. Note: only JS-capable crawlers (Googlebot) see these
// updates. Non-JS social scrapers (Twitter/X, Facebook, LinkedIn) only see the
// static fallback in index.html. Per-route social previews would require SSR
// or prerender — explicitly out of scope (Plan A, SEO-HYGIENE-3).

import { useEffect } from "react";

interface SeoProps {
  title: string;
  description?: string;
  canonical?: string;
  /** Open Graph type. Defaults to "website". */
  ogType?: "website" | "article";
  ogImage?: string;
  /** Alt text for the OG/Twitter image. */
  ogImageAlt?: string;
  /** og:site_name. Defaults to "Confetti". */
  siteName?: string;
  noindex?: boolean;
}

function upsertMeta(attr: "name" | "property", key: string, content: string): HTMLMetaElement {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
  return el;
}

function upsertLink(rel: string, href: string): HTMLLinkElement {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  return el;
}

export function Seo({
  title,
  description,
  canonical,
  ogType = "website",
  ogImage,
  ogImageAlt,
  siteName = "Confetti",
  noindex,
}: SeoProps) {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = title.length > 60 ? `${title.slice(0, 57)}…` : title;

    const desc = description?.slice(0, 160);
    if (desc) upsertMeta("name", "description", desc);
    if (noindex) upsertMeta("name", "robots", "noindex");

    // Open Graph
    upsertMeta("property", "og:site_name", siteName);
    upsertMeta("property", "og:title", title);
    if (desc) upsertMeta("property", "og:description", desc);
    upsertMeta("property", "og:type", ogType);
    if (ogImage) {
      upsertMeta("property", "og:image", ogImage);
      if (ogImageAlt) upsertMeta("property", "og:image:alt", ogImageAlt);
    }
    if (canonical) {
      upsertMeta("property", "og:url", canonical);
      upsertLink("canonical", canonical);
    }

    // Twitter / X — mirror per-route so JS-capable previewers see the right page.
    upsertMeta("name", "twitter:card", ogImage ? "summary_large_image" : "summary");
    upsertMeta("name", "twitter:title", title);
    if (desc) upsertMeta("name", "twitter:description", desc);
    if (ogImage) {
      upsertMeta("name", "twitter:image", ogImage);
      if (ogImageAlt) upsertMeta("name", "twitter:image:alt", ogImageAlt);
    }

    return () => {
      document.title = prevTitle;
      // Keep tags in place so subsequent pages can override them; only the
      // noindex flag is unique enough that we should clear it.
      if (noindex) {
        const robots = document.head.querySelector('meta[name="robots"]');
        robots?.parentElement?.removeChild(robots);
      }
    };
  }, [title, description, canonical, ogType, ogImage, ogImageAlt, siteName, noindex]);

  return null;
}
